"use client";

import type { AnchorHTMLAttributes } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockLogo } from "../../../shared/components/BlockLogo/BlockLogo";
import { MegaMenuItem } from "../MegaMenuItem/MegaMenuItem";
import type { IMegaMenuDesktopProps } from "./MegaMenuDesktop.props";

export const MegaMenuDesktop = ({
  actions,
  className,
  items,
  logo,
  sticky,
}: IMegaMenuDesktopProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const [openItemKey, setOpenItemKey] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const idPrefix = useId();

  const setTriggerRef = useCallback((itemKey: string, node: HTMLButtonElement | null) => {
    triggerRefs.current[itemKey] = node;
  }, []);

  const toggleItem = useCallback((itemKey: string) => {
    setOpenItemKey((currentItemKey) => (currentItemKey === itemKey ? null : itemKey));
  }, []);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia("(min-width: 64rem)");
    const closeWhenDesktopIsHidden = () => {
      if (!desktopMediaQuery.matches) {
        setOpenItemKey(null);
      }
    };

    closeWhenDesktopIsHidden();
    desktopMediaQuery.addEventListener("change", closeWhenDesktopIsHidden);

    return () => {
      desktopMediaQuery.removeEventListener("change", closeWhenDesktopIsHidden);
    };
  }, []);

  useEffect(() => {
    if (openItemKey === null) {
      return;
    }

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) {
        setOpenItemKey(null);
      }
    };
    const closeOnFocusOutside = (event: FocusEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) {
        setOpenItemKey(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      const trigger = triggerRefs.current[openItemKey];
      setOpenItemKey(null);
      trigger?.focus();
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("focusin", closeOnFocusOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("focusin", closeOnFocusOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openItemKey]);

  return (
    <BlockContainer
      as="header"
      disableSpacing
      fullWidth
      innerClassName="flex min-h-20 items-center gap-5 py-3 md:gap-7"
      parentClass={cn(
        "z-40 border-b border-foreground bg-background",
        sticky ? "sticky top-0" : "relative",
        className,
      )}
    >
      <div ref={menuRef} className="contents">
        {logo && Link ? (
          <Link
            aria-label="Home"
            className="relative h-8 w-40 shrink-0 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
            href="/"
          >
            <BlockLogo
              className="h-full w-full overflow-visible"
              imageClassName="object-contain object-left"
              loading="eager"
              media={logo}
              priority
            />
          </Link>
        ) : null}
        {items?.length ? (
          <nav aria-label="Primary navigation" className="min-w-0 flex-1">
            <ul className="flex list-none flex-wrap items-center gap-1">
              {items.map((item, index) => {
                const itemKey = String(index);
                const panelId = `${idPrefix}-panel-${index}`;
                const triggerId = `${idPrefix}-trigger-${index}`;

                return (
                  <MegaMenuItem
                    isOpen={openItemKey === itemKey}
                    item={item}
                    itemKey={itemKey}
                    key={itemKey}
                    onToggle={toggleItem}
                    panelId={panelId}
                    setTriggerRef={setTriggerRef}
                    triggerId={triggerId}
                  />
                );
              })}
            </ul>
          </nav>
        ) : null}
        {actions?.length ? <BlockActions actions={actions} className="ml-auto shrink-0" /> : null}
      </div>
    </BlockContainer>
  );
};

MegaMenuDesktop.displayName = "MegaMenu::MegaMenuDesktop";
